import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import PublicForm from "@/pages/PublicForm";
import QuickLead from "@/pages/QuickLead";
import Login from "@/pages/Login";
import TrackQuote from "@/pages/TrackQuote";
import BidPortal from "@/pages/BidPortal";
import VendorDocsPage from "@/pages/VendorDocsPage";
import DashboardLayout from "@/components/DashboardLayout";
import DashboardHome from "@/pages/DashboardHome";
import LeadsPage from "@/pages/LeadsPage";
import UsersPage from "@/pages/UsersPage";
import VendorsPage from "@/pages/VendorsPage";
import AccountsPage from "@/pages/AccountsPage";
import WhatsAppBid from "@/pages/WhatsAppBid";
import FeedbackPage from "@/pages/FeedbackPage";
import DriverTrack from "@/pages/DriverTrack";
import TrackingPage from "@/pages/TrackingPage";
import ConsignmentTrack from "@/pages/ConsignmentTrack";
import PickupTrackPage from "@/pages/PickupTrackPage";
import PickupDropBookingsPage from "@/pages/PickupDropBookingsPage";
import PartnerFeedbackPage from "@/pages/PartnerFeedbackPage";
import VendorQuotesPage from "@/pages/VendorQuotesPage";
import PackingSchedule from "@/pages/UpcomingOrders";
import ILOTPage from "@/pages/ILOTPage";
import VendorPaymentsPage from "@/pages/VendorPaymentsPage";
import VehiclesPage from "@/pages/VehiclesPage";
import OfficeManagementPage from "@/pages/OfficeManagementPage";
import HistoryPage from "@/pages/HistoryPage";
import CustomersPage from "@/pages/CustomersPage";
import ThreePLEnquiriesPage from "@/pages/ThreePLEnquiriesPage";
import AggOverviewPage from "@/pages/agg/AggOverviewPage";
import AggShipmentsPage from "@/pages/agg/AggShipmentsPage";
import AggFranchisePage from "@/pages/agg/AggFranchisePage";
import AggRtoPage from "@/pages/agg/AggRtoPage";
import AggExceptionsPage from "@/pages/agg/AggExceptionsPage";
import AggMastersPage from "@/pages/agg/AggMastersPage";
import AggFinancePage from "@/pages/agg/AggFinancePage";
import AggAnalyticsPage from "@/pages/agg/AggAnalyticsPage";
import PdOpsPage from "@/pages/ops/PdOpsPage";
import PmOpsPage from "@/pages/ops/PmOpsPage";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import SignUp from "@/pages/customer/SignUp";
import SignIn from "@/pages/customer/SignIn";
import ForgotPassword from "@/pages/customer/ForgotPassword";
import ResetPassword from "@/pages/customer/ResetPassword";
import MyAccount from "@/pages/customer/MyAccount";

function Protected({ children, roles }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  useEffect(() => {
    if (localStorage.getItem("arrow_theme") !== "light") document.documentElement.classList.add("dark");
  }, []);
  return (
    <div className="App">
      <AuthProvider>
      <CustomerAuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PublicForm />} />
            <Route path="/quick-lead" element={<QuickLead />} />
            <Route path="/login" element={<Login />} />
            <Route path="/track/:token" element={<TrackQuote />} />
            <Route path="/track-shipment" element={<ConsignmentTrack />} />
            <Route path="/pickup-track/:token" element={<PickupTrackPage />} />
            <Route path="/bid/:token" element={<BidPortal />} />
            <Route path="/vendor-docs/:token" element={<VendorDocsPage />} />
            <Route path="/wq/:token" element={<WhatsAppBid />} />
            <Route path="/feedback/:token" element={<FeedbackPage />} />
            <Route path="/drive/:token" element={<DriverTrack />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/account/forgot-password" element={<ForgotPassword />} />
            <Route path="/account/reset-password" element={<ResetPassword />} />
            <Route path="/my-account" element={<MyAccount />} />
            <Route
              path="/dashboard"
              element={
                <Protected>
                  <DashboardLayout />
                </Protected>
              }
            >
              <Route index element={<DashboardHome />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="ilot" element={<ILOTPage />} />
              <Route path="upcoming" element={<PackingSchedule />} />
              <Route
                path="users"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <UsersPage />
                  </Protected>
                }
              />
              <Route
                path="customers"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <CustomersPage />
                  </Protected>
                }
              />
              <Route
                path="accounts"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <AccountsPage />
                  </Protected>
                }
              />
              <Route
                path="vendor-payments"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <VendorPaymentsPage />
                  </Protected>
                }
              />
              <Route
                path="vendors"
                element={
                  <Protected roles={["admin", "manager", "calling_executive"]}>
                    <VendorsPage />
                  </Protected>
                }
              />
              <Route
                path="vehicles"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <VehiclesPage />
                  </Protected>
                }
              />
              <Route
                path="office"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <OfficeManagementPage />
                  </Protected>
                }
              />
              <Route
                path="history"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <HistoryPage />
                  </Protected>
                }
              />
              <Route
                path="tracking"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <TrackingPage />
                  </Protected>
                }
              />
              <Route
                path="quotes"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <VendorQuotesPage />
                  </Protected>
                }
              />
              <Route
                path="feedback"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <PartnerFeedbackPage />
                  </Protected>
                }
              />
              <Route
                path="3pl-enquiries"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <ThreePLEnquiriesPage />
                  </Protected>
                }
              />
              <Route
                path="pickup-drop"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <PickupDropBookingsPage />
                  </Protected>
                }
              />
              <Route path="agg" element={<Protected roles={["admin", "manager"]}><AggOverviewPage /></Protected>} />
              <Route path="agg/shipments" element={<Protected roles={["admin", "manager"]}><AggShipmentsPage /></Protected>} />
              <Route path="agg/franchise" element={<Protected roles={["admin", "manager"]}><AggFranchisePage /></Protected>} />
              <Route path="agg/rto" element={<Protected roles={["admin", "manager"]}><AggRtoPage /></Protected>} />
              <Route path="agg/exceptions" element={<Protected roles={["admin", "manager"]}><AggExceptionsPage /></Protected>} />
              <Route path="agg/finance" element={<Protected roles={["admin", "manager"]}><AggFinancePage /></Protected>} />
              <Route path="agg/analytics" element={<Protected roles={["admin", "manager"]}><AggAnalyticsPage /></Protected>} />
              <Route path="ops/pd" element={<Navigate to="/dashboard/ops/pd/overview" replace />} />
              <Route path="ops/pd/:tab" element={<Protected roles={["admin", "manager"]}><PdOpsPage /></Protected>} />
              <Route path="ops/pm" element={<Navigate to="/dashboard/ops/pm/overview" replace />} />
              <Route path="ops/pm/:tab" element={<Protected roles={["admin", "manager"]}><PmOpsPage /></Protected>} />
              <Route path="agg/masters" element={<Protected roles={["admin", "manager"]}><AggMastersPage /></Protected>} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors theme="dark" />
      </CustomerAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
